import React from 'react';

import type { AiChatReference, Insight, InsightExpertPayload } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';
import { useInsightsData } from '~/components/ClaimPage/CliveApp/CliveInsights/contexts/InsightsDataProvider';
import { ComplexityBadge } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/ComplexityBadge';
import { InsightCardBody } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/InsightCardBody';
import { InsightHistoryTimeline } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/InsightHistoryTimeline';
import { INSIGHT_ICONS_V2 } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/insightIconsV2';
import { LatestVersionBadge } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/LatestVersionBadge';
import mixpanel from '~/components/CmsMain/mixpanel';
import Button from '~/components/core/Atomic/Buttons/Button';
import { Text } from '~/components/core/TextComponents';
import { useCms } from '~/components/hooks/useCms';
import ChevronDownIcon from '~/components/icons/ChevronDownIcon';
import InProgressIcon from '~/components/icons/InProgressIcon';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import MIXPANEL_EVENTS from '~/server_shared/generated-types/MIXPANEL_EVENTS';
import cn from '~/Utils/cn';

interface InsightCardV2Props {
  insight: Insight;
  references: Record<string, AiChatReference>;
}

/**
 * New-UI presentation of a single insight, split into three sections:
 *  - Header (always visible, clickable to expand): the type icon (left) with the title + complexity
 *    badge + "Updated on" timestamp, and the sub_title underneath. Dismiss action + "New" marker on the right.
 *  - Body (visible only when expanded): the insight content (markdown / claim action plan) and references.
 *  - History (visible only when opened): the older versions of this insight, newest first.
 *
 * The insight this renders is the *latest* version of its key; the timeline below holds the rest.
 * Dismiss is shared via the insights data hook.
 */
export const InsightCardV2: React.FC<InsightCardV2Props> = ({ insight, references }) => {
  const { dismissInsight, loadMoreHistory, history } = useInsightsData();
  const { claim } = useClaim({ claimRequired: true });
  const { userOrganization } = useCms();
  const { insight_type, insight_key, sub_title, created_at } = insight;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);
  const isDismissed = Boolean(insight.is_dismissed);

  // Undefined is "not reported", not "there are none" — folding the two together would disable the
  // control on every card and make the timeline unreachable.
  const historyCount = insight.history_count;
  const hasNoHistory = historyCount === 0;
  //const historyCountLabel = historyCount === undefined ? '' : ` (${historyCount})`; // TODO: Add back in when implementing filter of dismissed/undismissed

  const complexity =
    insight_key === 'complexity' ? ((insight.insight_metadata as Record<string, unknown>)?.complexity as string) : null;

  const payload = insight.payload as InsightExpertPayload | undefined;
  const insightIcon = payload?.icon;

  const trackingProperties = {
    expert_key: payload?.expert_key,
    insight_key,
    claim_id: claim.id,
    lob: claim.lob,
    org_id: userOrganization.id,
  };

  const handleToggle = (): void => {
    const willOpen = !isOpen;
    if (willOpen) {
      // Count opens only (not collapses) — matches the card-open-rate metric numerator.
      mixpanel.track(MIXPANEL_EVENTS.INSIGHT_EVENTS.INSIGHT_CARD_EXPANDED, trackingProperties);
    }
    setIsOpen(willOpen);
  };

  const handleHistoryToggle = (): void => {
    const willOpen = !isHistoryOpen;
    if (willOpen) {
      mixpanel.track(MIXPANEL_EVENTS.INSIGHT_EVENTS.INSIGHT_HISTORY_VIEWED, {
        ...trackingProperties,
        history_count: historyCount,
      });
      // First page only, and only on the first open: the provider's guard stops at the last page,
      // so without this a reopen would append a page nobody scrolled for. The sentinel asks for the
      // rest.
      if (!history[insight_key]) {
        loadMoreHistory(insight);
      }
    }
    setIsHistoryOpen(willOpen);
  };

  return (
    <li className="animate-fadeIn flex list-none flex-col gap-12 rounded-xl border-1 border-solid border-blue-200 bg-white p-24">
      {/* Header */}
      <div className="flex items-center gap-16">
        <button
          type="button"
          onClick={handleToggle}
          className="flex grow cursor-pointer items-center gap-16 border-0 bg-transparent p-0 text-left"
        >
          <div className="flex shrink-0 items-center justify-center rounded-lg bg-azure-200 p-8">
            {(insightIcon && INSIGHT_ICONS_V2[insightIcon]) ||
              INSIGHT_ICONS_V2[insight_type] ||
              INSIGHT_ICONS_V2[insight_key]}
          </div>

          <span className="flex grow flex-col gap-2">
            <span className="flex flex-wrap items-center gap-8">
              <Text variant={Text.VARIANTS.XL} weight={Text.WEIGHTS.SEMI_BOLD} colorVariant={Text.COLOR_VARIANTS.BLACK}>
                {insight.title}
              </Text>
              {complexity ? <ComplexityBadge complexity={complexity} /> : null}
              {/* Deliberately created_at, despite the "Updated on" label: insights are append-only,
                  so updated_at only moves when this version is dismissed, while created_at is when
                  it was actually generated. */}
              {created_at ? (
                <span className="flex items-center gap-4">
                  <InProgressIcon size={14} className="grey-500" />
                  <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                    Updated on {serverDateTimeToLocal(created_at)}
                  </Text>
                </span>
              ) : null}
            </span>
            {sub_title ? (
              <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.PRIMARY}>
                {sub_title}
              </Text>
            ) : null}
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-16">
          <Button
            variant="text"
            className={cn('p-0 font-medium normal-case text-pink-600 hover:bg-transparent disabled:text-pink-600', {
              'opacity-50': isDismissed,
            })}
            onClick={() => dismissInsight(insight)}
            disabled={isDismissed}
          >
            {isDismissed ? 'Dismissed' : 'Dismiss'}
          </Button>
          {!isDismissed ? <LatestVersionBadge /> : null}
        </div>
      </div>

      {/* Body — visible only when expanded */}
      {isOpen ? <InsightCardBody insight={insight} references={references} /> : null}

      {/* History — the control stays visible and disabled at zero, so the absence of previous
          versions is stated rather than left ambiguous. */}
      <div className="flex flex-col gap-12">
        <Button
          variant="text"
          className="w-fit p-0 font-semibold uppercase tracking-wide text-teal-700 hover:bg-transparent disabled:text-grey-400"
          onClick={handleHistoryToggle}
          disabled={hasNoHistory}
          startIcon={
            <ChevronDownIcon
              size={15}
              iconColor="currentColor"
              className={cn('flex transition-transform', { 'rotate-180': isHistoryOpen })}
            />
          }
        >
          {isHistoryOpen ? 'Hide' : 'View'} history {/*{historyCountLabel}*/}
        </Button>

        {isHistoryOpen ? <InsightHistoryTimeline insight={insight} references={references} /> : null}
      </div>
    </li>
  );
};
